/**
 * dealStatusField — put the deal's own status into a CHILD entity's dialog.
 *
 * Feedback #26: logging a contact or submitting an approval is usually the
 * moment the deal's status changes, and today that means saving the dialog,
 * leaving the tab, and editing the deal. The status belongs where the decision
 * is made.
 *
 * WHY THIS IS NOT JUST ANOTHER METADATA FIELD
 * `deal_status` lives on `deals`; the dialog edits `deal_contact_log` or
 * `approvals`. So one Save becomes two writes to two endpoints, and the
 * interesting question is what happens when the second one fails.
 *
 * THE ORDER IS DELIBERATE: the deal's status is written FIRST, and a failure
 * there aborts the whole save — nothing is written, the dialog stays open, the
 * user sees why. The alternative (entry first) can leave a logged contact
 * whose status change silently did not happen, which is worse: the entry reads
 * as proof the status moved.
 *
 * The residual risk is the mirror image — status written, entry fails — and it
 * is the lesser one: the status is visible in the header, so a user can see it
 * moved and retry the note. It is not silent.
 */
import { DEAL_STATUSES } from "./dealStatusVocab";

/** The synthetic field's name. Matches the column on `deals`. */
export const DEAL_STATUS_FIELD = "deal_status";

/**
 * A metadata row for the deal's status, to append to a child entity's
 * metadata.
 *
 * `hide: true` keeps it out of the GRID — the panel's columns come from
 * `metadataToAntdColumns`, which drops hidden fields, and a deal-level value
 * repeated down every row of a child grid would read as a property of the row.
 * {@link dealStatusFormFilter} is what lets it back into the form.
 *
 * @param {number} ord — sort position; pass one past the entity's last field
 */
export const dealStatusMetadataRow = (ord) => ({
  field: DEAL_STATUS_FIELD,
  label: "Deal Status",
  data_type: "Lookup",
  input_type: "select",
  options: DEAL_STATUSES,
  hide: true,
  editable: true,
  ord,
  definition:
    "The deal's own status. Changing it here updates the deal, not this record.",
});

// Mirrors the library panel's default form exclusion, which it does not export
// (same local copy as components/interestInput).
const SERVER_MANAGED_FIELDS = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
]);

/**
 * Form-field predicate that admits the synthetic row despite `hide`, and
 * otherwise reproduces the library's default (`!row.hide && !SERVER_MANAGED`).
 *
 * The server-managed exclusion is not redundant: migration 050 un-hid the
 * contact log's created_by / created_at ("Logged By" / "Logged At") so the
 * grid shows them, and a `!row.hide`-only filter then put them in the Add
 * dialog — where the empty date posted `created_at: null` and the
 * `extra="forbid"` create model 422'd. Server-stamped columns are never inputs.
 */
export const dealStatusFormFilter = (row) =>
  row?.field === DEAL_STATUS_FIELD ||
  (!row?.hide && !SERVER_MANAGED_FIELDS.has(row?.field));

/**
 * Stamp the deal's current status onto each child row so the dialog opens
 * pre-filled rather than blank.
 *
 * The rows are not otherwise touched, and the field never reaches the grid.
 */
export const withDealStatus = (rows, dealStatus) =>
  (Array.isArray(rows) ? rows : []).map((r) => ({
    ...r,
    [DEAL_STATUS_FIELD]: dealStatus ?? null,
  }));

/**
 * Split a submitted body into the deal's status and the child entity's own
 * fields.
 *
 * @returns {{ status: string|undefined, body: object }}
 *   `status` is undefined when the field was absent or unchanged — the caller
 *   then skips the deal write entirely rather than issuing a no-op PUT.
 */
export const splitDealStatus = (body, currentStatus) => {
  const { [DEAL_STATUS_FIELD]: submitted, ...rest } = body || {};
  const changed =
    submitted !== undefined && submitted !== null && submitted !== currentStatus;
  return { status: changed ? submitted : undefined, body: rest };
};
